// frontend/src/hooks/useEmergencyContactsData.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { confidentialAPI } from '../services/api';
import toast from '../utils/toast';

const invalidateEmergencyContactQueries = (queryClient, staffId) => {
  queryClient.invalidateQueries({ queryKey: ['emergency', 'contacts'] });
  if (staffId) {
    queryClient.invalidateQueries({ queryKey: ['emergency', 'contacts', 'staff', staffId] });
  }
};

/**
 * Hook to fetch emergency contacts for the current user
 * Follows project pattern from useLeaveData.js and useStaffData.js
 */
export const useEmergencyContacts = () => {
  return useQuery({
    queryKey: ['emergency', 'contacts'],
    queryFn: async () => {
      const response = await confidentialAPI.getEmergencyContacts();
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - matches project standard for HR data
  });
};

/**
 * Hook to fetch emergency contacts for a specific staff member (admin use)
 * Separate from useEmergencyContacts to avoid cache collisions with self-view.
 * 
 * @param {string} staffId - The staff_id of the employee to fetch contacts for
 */
export const useEmergencyContactsForStaff = (staffId) => {
  return useQuery({
    queryKey: ['emergency', 'contacts', 'staff', staffId],
    queryFn: async () => {
      const response = await confidentialAPI.getEmergencyContacts({
        emergency_contact_staff_id: staffId,
      });
      return response.data?.results || response.data || [];
    },
    enabled: !!staffId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to create a new emergency contact.
 * Pass staffId when HR is writing onto another employee's file.
 */
export const useCreateEmergencyContact = (staffId) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => confidentialAPI.createEmergencyContact(
      staffId ? { ...data, emergency_contact_staff_id: staffId } : data
    ),
    onSuccess: () => {
      invalidateEmergencyContactQueries(queryClient, staffId);
      toast.success('Contact added successfully');
    },
    onError: (error) => {
      console.error('Error creating emergency contact:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          'Failed to add contact';
      toast.error(errorMessage);
    },
  });
};

/**
 * Hook to update an existing emergency contact
 */
export const useUpdateEmergencyContact = (staffId) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.updateEmergencyContact(id, data),
    onSuccess: () => {
      invalidateEmergencyContactQueries(queryClient, staffId);
      toast.success('Contact updated successfully');
    },
    onError: (error) => {
      console.error('Error updating emergency contact:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          'Failed to update contact';
      toast.error(errorMessage);
    },
  });
};

/**
 * Hook to delete an emergency contact
 */
export const useDeleteEmergencyContact = (staffId) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => confidentialAPI.deleteEmergencyContact(id),
    onSuccess: () => {
      invalidateEmergencyContactQueries(queryClient, staffId);
      toast.success('Contact deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting emergency contact:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          'Failed to delete contact';
      toast.error(errorMessage);
    },
  });
};
